export type ConversationFilterBlockId =
  | 'status'
  | 'assignment'
  | 'source'
  | 'sender';

export type ConversationFilterBlock = {
  id: ConversationFilterBlockId;
  values: string[];
  defaultValue?: string;
};

export const conversationFilterBlocks: ConversationFilterBlock[] = [
  {
    id: 'status',
    values: ['all', 'needsResponse', 'snoozed', 'closed', 'inactive'],
    defaultValue: 'all',
  },
  {
    id: 'assignment',
    values: ['assigned', 'assignedToMyTeam', 'unassigned'],
  },
  {
    id: 'source',
    values: ['fromEmail', 'fromArk'],
  },
  {
    id: 'sender',
    values: ['resident', 'leaseholder', 'contractor', 'internal', 'unknown'],
  },
];

const blockForValue = (value: string): ConversationFilterBlock | undefined =>
  conversationFilterBlocks.find(block => block.values.includes(value));

export type ActiveConversationFilters = Record<
  ConversationFilterBlockId,
  string | undefined
>;

export const deriveActiveFilters = (
  filterValues: string[],
): ActiveConversationFilters => {
  const active = {} as ActiveConversationFilters;
  for (const block of conversationFilterBlocks) {
    active[block.id] = block.defaultValue;
  }
  for (const value of filterValues) {
    const block = blockForValue(value);
    if (block) {
      active[block.id] = value;
    }
  }
  return active;
};

export const buildAdditiveFilterValues = (
  currentFilterValues: string[],
  targetValue: string,
): string[] => {
  const targetBlock = blockForValue(targetValue);
  if (!targetBlock) return currentFilterValues;

  const preserved = currentFilterValues.filter(
    value => !targetBlock.values.includes(value),
  );

  const isDefault = targetValue === targetBlock.defaultValue;
  const isAlreadyActive = currentFilterValues.includes(targetValue);
  if (isDefault || isAlreadyActive) return preserved;
  return [...preserved, targetValue];
};

export const buildAdditiveFilterHref = (
  path: string,
  currentSearchParams: URLSearchParams,
  targetValue: string,
): string => {
  const next = new URLSearchParams(currentSearchParams);
  next.delete('filter');

  const nextFilters = buildAdditiveFilterValues(
    currentSearchParams.getAll('filter'),
    targetValue,
  );
  for (const value of nextFilters) {
    next.append('filter', value);
  }

  const query = next.toString();
  return query ? `${path}?${query}` : path;
};
